import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Plus, ArrowRight, LogOut, Trash2, Shield } from "lucide-react";

import * as api from "../api";
import { useAuth } from "../auth";

const STATUS_STYLES = {
  DRAFT: "bg-slate-100 text-slate-700",
  INTAKE_IN_PROGRESS: "bg-amber-100 text-amber-900",
  INTAKE_COMPLETE: "bg-emerald-100 text-emerald-800",
  PAID_HANDED_OFF: "bg-emerald-100 text-emerald-800",
  IN_FILING: "bg-blue-100 text-blue-800",
  INCORPORATED: "bg-blue-100 text-blue-800",
  PURGED: "bg-slate-200 text-slate-500",
};

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("en-IN",
    { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
}

export default function DashboardPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [cases, setCases] = useState(null);
  const [retentions, setRetentions] = useState({}); // case_id -> {retain_until, can_user_erase, purged}
  const [busy, setBusy] = useState(false);
  const [erasing, setErasing] = useState(null);

  async function refresh() {
    const list = await api.listCases();
    setCases(list);
    // Fetch retention for each case (parallel)
    const entries = await Promise.all(
      list.map((c) => api.getRetention(c.case_id)
        .then((r) => [c.case_id, r])
        .catch(() => [c.case_id, null])));
    setRetentions(Object.fromEntries(entries.filter(([, v]) => v)));
  }

  useEffect(() => { refresh().catch(() => setCases([])); }, []);

  async function startNew() {
    setBusy(true);
    try {
      const c = await api.createCase();
      nav(`/cases/${c.case_meta.case_id}`);
    } catch (e) {
      toast.error("Could not create case");
    } finally {
      setBusy(false);
    }
  }

  async function eraseCase(caseId, e) {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(
      "Delete all data for this case?\n\n" +
      "We will hard-delete uploaded documents, encrypted Aadhaar entries, " +
      "and personal details. An anonymised analytics stub will remain. " +
      "This cannot be undone."
    )) return;
    setErasing(caseId);
    try {
      await api.eraseCase(caseId);
      toast.success("Data deleted. Audit log records this action.");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    } finally {
      setErasing(null);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--canvas)]">
      {/* Header */}
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-900">
            <Building2 size={22} className="text-[#C5283F]" />
            <span className="font-display text-xl font-semibold">Infin Entities</span>
          </Link>
          <div className="flex items-center gap-3">
            {user?.picture && (
              <img src={user.picture} alt="" data-testid="user-avatar"
                   className="w-7 h-7 rounded-full ring-1 ring-slate-200 object-cover" />
            )}
            <span className="text-xs text-slate-500 hidden sm:inline" data-testid="user-email-label">{user?.email}</span>
            <button onClick={() => { logout(); nav("/"); }}
                    data-testid="dashboard-logout-btn"
                    className="text-sm text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
              Welcome back, {user?.display_name?.split(" ")[0] || "founder"}.
            </h1>
            <p className="text-slate-500 mt-2 max-w-2xl">
              Start a new incorporation or pick up where you left off. Infi will guide you
              through every step — most founders finish intake in 20–30 minutes.
            </p>
          </div>
          <button
            onClick={startNew}
            disabled={busy}
            data-testid="start-new-case-btn"
            className="bg-[#C5283F] text-white px-5 py-3 rounded-lg font-medium hover:bg-[#A82035] disabled:opacity-50 inline-flex items-center gap-2 shadow-sm"
          >
            <Plus size={16} /> Start new incorporation
          </button>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div className="font-display text-lg font-semibold">Your cases</div>
            <div className="text-xs text-slate-500">{cases?.length || 0} total</div>
          </div>
          {cases === null && <div className="p-8 text-sm text-slate-500">Loading…</div>}
          {cases?.length === 0 && (
            <div className="p-12 text-center">
              <div className="text-slate-500 mb-3">You don&apos;t have any cases yet.</div>
              <button onClick={startNew}
                      data-testid="empty-start-btn"
                      className="text-[#C5283F] font-medium underline">
                Start your first incorporation →
              </button>
            </div>
          )}
          {(cases || []).map((c) => {
            const ret = retentions[c.case_id] || {};
            const purged = ret.purged;
            return (
            <div
              key={c.case_id}
              data-testid={`case-row-${c.case_id}`}
              className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors group"
            >
              <Link to={purged ? "#" : `/cases/${c.case_id}`}
                    onClick={(e) => purged && e.preventDefault()}
                    className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900">
                  {c.company_name || "Untitled company"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 font-mono">{c.case_id}</div>
                {ret.retain_until && !purged && (
                  <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1"
                       data-testid={`retain-until-${c.case_id}`}>
                    <Shield size={10} /> Data retained until {fmtDate(ret.retain_until)}
                  </div>
                )}
                {purged && (
                  <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                    Data deleted on {fmtDate(ret.purged_at)}
                  </div>
                )}
              </Link>
              <div className="flex items-center gap-3">
                <span className={[
                  "text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full",
                  STATUS_STYLES[c.status] || "bg-slate-100 text-slate-700",
                ].join(" ")}>
                  {(c.status || "").replace(/_/g, " ").toLowerCase()}
                </span>
                {ret.can_user_erase && !purged && (
                  <button
                    onClick={(e) => eraseCase(c.case_id, e)}
                    disabled={erasing === c.case_id}
                    data-testid={`erase-case-btn-${c.case_id}`}
                    title="Delete all data for this case"
                    className="text-slate-400 hover:text-red-700 transition-colors disabled:opacity-50"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
                {!purged && <ArrowRight size={16} className="text-slate-400" />}
              </div>
            </div>
          );})}
        </div>
        <div className="mt-6 text-[11px] text-slate-400 leading-relaxed max-w-2xl"
             data-testid="dpdp-note">
          We follow India's Digital Personal Data Protection Act, 2023. Identity
          documents are deleted automatically when a case is abandoned (60 days
          inactive) or after the retention window post-incorporation. You can
          delete any unpaid case anytime via the trash icon. Questions to{" "}
          <a href="mailto:grievance@tryinfin.com" className="underline hover:text-slate-600">
            grievance@tryinfin.com
          </a>.
        </div>
      </div>
    </div>
  );
}
